/**
 * <Switch> — l'interrupteur contrôlé (atom).
 *
 * Le pendant « oui/non » de `<Checkbox>` : là où la case coche un élément d'une liste,
 * l'interrupteur bascule un réglage — l'accusé de perte de créneau d'une pause, le « zéro
 * engagement » du composeur. Il ne possède pas son état : `checked` + `onChange`.
 *
 * Maison, pas le `<Switch>` de React Native : le livre chiffre la piste et le galet, et
 * l'anneau de focus jaune est un atome à nous. Au clavier, le web lui donne le focus comme à
 * un bouton (`<Pressable>` est focusable) et Entrée / Espace le basculent.
 *
 * Géométrie intrinsèque en nombres nus (piste, galet, marge) : ce sont des proportions du
 * dessin, pas des décisions qu'un jeton porterait (contrat § 2). Le galet glisse en `theme.motion.micro`
 * (150 ms, le « micro » du livre), et pas du tout quand le système demande moins d'animation.
 */
import React, { useEffect, useRef } from "react"
import { Animated, Easing, Platform, Pressable, View } from "react-native"
import { StyleSheet, useUnistyles } from "react-native-unistyles"

import { type SemanticColorKey } from "../../theme/tokens/colors"
import { useReduceMotion } from "../../hooks/useReduceMotion"
import { FocusRing, focusRingHost } from "../FocusRing"
import { Row } from "../containers/Row"
import { Text } from "../typography/Text"

/** `<Pressable>` ne type que `pressed` ; react-native-web ajoute `focused` à l'exécution. */
type PressableVisualState = { pressed: boolean; focused?: boolean }

/** Piste, galet et marges — la géométrie du dessin. */
const TRACK_WIDTH = 48
const TRACK_HEIGHT = 28
const TRACK_BORDER = 1
const THUMB_SIZE = 22
const INSET = (TRACK_HEIGHT - TRACK_BORDER * 2 - THUMB_SIZE) / 2
const TRAVEL = TRACK_WIDTH - TRACK_BORDER * 2 - THUMB_SIZE - INSET * 2

export type SwitchProps = {
    checked: boolean
    onChange: (next: boolean) => void
    /** Libellé à droite de l'interrupteur — il fait partie de la zone tapable. */
    label?: string
    /** Nom lu par un lecteur d'écran quand l'interrupteur n'a pas de libellé visible. Défaut : `label`. */
    accessibilityLabel?: string
    disabled?: boolean
    testID?: string
}

export function Switch(props: SwitchProps) {
    const { checked, onChange, label, accessibilityLabel = label, disabled = false, testID } = props

    const reduceMotion = useReduceMotion()
    const { motion } = useUnistyles().theme
    const progress = useRef(new Animated.Value(checked ? 1 : 0)).current

    useEffect(() => {
        Animated.timing(progress, {
            toValue: checked ? 1 : 0,
            duration: reduceMotion ? 0 : motion.micro,
            easing: Easing.bezier(...motion.easing),
            // react-native-web n'a pas de pilote natif ; y prétendre ne fait qu'avertir.
            useNativeDriver: Platform.OS !== "web",
        }).start()
    }, [checked, reduceMotion, progress, motion])

    /**
     * Éteint, le galet est marine-gris sur piste claire ; allumé, il est blanc sur la couleur de
     * l'univers. Un galet blanc sur une piste grise tombait à 1,3 de contraste — le livre exige
     * l'AA « partout », et c'est le galet qui porte l'information.
     */
    const trackColor: SemanticColorKey = checked ? "role" : "surface-subtle"
    const thumbColor: SemanticColorKey = checked ? "surface" : "text-muted"

    return (
        <Pressable
            testID={testID}
            disabled={disabled}
            onPress={() => onChange(!checked)}
            accessibilityRole="switch"
            accessibilityLabel={accessibilityLabel}
            accessibilityState={{ checked, disabled }}
            style={({ pressed }: PressableVisualState) => [
                styles.root(disabled),
                focusRingHost,
                pressed && !disabled ? styles.pressed : undefined,
            ]}
        >
            {({ focused }: PressableVisualState) => (
                <Row gap="3" align="center">
                    {/* L'anneau se pose sur la boîte de la piste, pas sur sa zone intérieure. */}
                    <View>
                        <FocusRing visible={focused === true && !disabled} radius={TRACK_HEIGHT / 2} />
                        <View style={styles.track(trackColor, checked)}>
                            <Animated.View
                                style={[
                                    styles.thumb(thumbColor),
                                    {
                                        transform: [
                                            {
                                                translateX: progress.interpolate({
                                                    inputRange: [0, 1],
                                                    outputRange: [0, TRAVEL],
                                                }),
                                            },
                                        ],
                                    },
                                ]}
                            />
                        </View>
                    </View>
                    {label != null && (
                        <Text size="text-base" color="text-primary" style={styles.label}>
                            {label}
                        </Text>
                    )}
                </Row>
            )}
        </Pressable>
    )
}

const styles = StyleSheet.create((theme) => ({
    root: (disabled: boolean) => ({
        opacity: disabled ? 0.5 : 1,
        alignSelf: "flex-start" as const,
    }),
    pressed: { opacity: 0.85 },
    track: (color: SemanticColorKey, checked: boolean) => ({
        width: TRACK_WIDTH,
        height: TRACK_HEIGHT,
        borderRadius: TRACK_HEIGHT / 2,
        backgroundColor: theme.colors[color],
        borderWidth: TRACK_BORDER,
        borderColor: checked ? theme.colors.role : theme.colors.border,
        justifyContent: "center" as const,
        paddingHorizontal: INSET,
    }),
    thumb: (color: SemanticColorKey) => ({
        width: THUMB_SIZE,
        height: THUMB_SIZE,
        borderRadius: THUMB_SIZE / 2,
        backgroundColor: theme.colors[color],
    }),
    label: { flexShrink: 1 },
}))
